import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

const GUNLER = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];
const GUN_LABELS: any = { Pzt:'Pazartesi',Sal:'Salı',Çar:'Çarşamba',Per:'Perşembe',Cum:'Cuma',Cmt:'Cumartesi',Paz:'Pazar' };
const RENKLER = [
  {bg:'#EEEDFE',text:'#3C3489',bar:'#534AB7'},
  {bg:'#E1F5EE',text:'#085041',bar:'#1D9E75'},
  {bg:'#FBEAF0',text:'#72243E',bar:'#D4537E'},
  {bg:'#FAEEDA',text:'#854F0B',bar:'#EF9F27'},
  {bg:'#E6F1FB',text:'#0C447C',bar:'#378ADD'},
];
const ETK_RENK: Record<string, {bg:string;text:string;bar:string}> = {
  milonga:  {bg:'#F3F0FF',text:'#6B46C1',bar:'#6B46C1'},
  pratik:   {bg:'#EFF6FF',text:'#2563EB',bar:'#2563EB'},
  kiralama: {bg:'#FFF7ED',text:'#EA580C',bar:'#EA580C'},
  diger:    {bg:'#F3F4F6',text:'#6B7280',bar:'#6B7280'},
};
const ETK_LABEL: Record<string, string> = { milonga:'Milonga', pratik:'Pratik', kiralama:'Kiralama', diger:'Diğer' };
const bugunGun = ['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'][new Date().getDay()];

const Program: React.FC = () => {
  usePageTitle('Program');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const [dersler, setDersler] = useState<any[]>([]);
  const [salonlar, setSalonlar] = useState<any[]>([]);
  const [etkinlikler, setEtkinlikler] = useState<any[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [gorunum, setGorunum] = useState<'salon'|'liste'>('salon');
  const [seciliSalon, setSeciliSalon] = useState<string>('tumu');
  const [seciliGun, setSeciliGun] = useState<string>(bugunGun);

  useEffect(() => {
    Promise.all([
      api.get('/api/dersler'),
      api.get('/api/salonlar'),
      api.get('/api/etkinlikler').catch(() => ({ data: [] })),
    ])
      .then(([d, s, e]) => { setDersler(d.data); setSalonlar(s.data); setEtkinlikler(e.data); })
      .finally(() => setYukleniyor(false));
  }, []);

  const getSalonRenk = (salonId: string) => {
    const idx = salonlar.findIndex((s:any) => s.id === salonId);
    return RENKLER[idx % RENKLER.length] || RENKLER[0];
  };

  const tekrarlayanEtk = useMemo(() => etkinlikler.filter(e => e.tekrarlayan), [etkinlikler]);

  const toMin = (t: string) => {
    const s = String(t);
    return parseInt(s.slice(0,2)) * 60 + parseInt(s.slice(3,5) || '0');
  };

  // Zaman aralığı: tüm ders ve etkinliklerin min başlangıç / max bitiş
  const { gridBas, gridBit } = useMemo(() => {
    let mn = 16 * 60, mx = 23 * 60;
    dersler.forEach(d => d.seanslar?.forEach((s:any) => {
      const bs = toMin(s.baslangic_saat), bt = toMin(s.bitis_saat);
      if (bs < mn) mn = bs; if (bt > mx) mx = bt;
    }));
    tekrarlayanEtk.forEach(e => {
      if (e.tum_gun) { mn = Math.min(mn, 0); mx = Math.max(mx, 24*60); return; }
      const bs = toMin(e.baslangic_saat), bt = toMin(e.bitis_saat);
      if (bs < mn) mn = bs; if (bt > mx) mx = bt;
    });
    return { gridBas: Math.floor(mn / 60) * 60, gridBit: Math.ceil(mx / 60) * 60 };
  }, [dersler, tekrarlayanEtk]);

  const SAAT_YUK = 52; // px per hour
  const gridHeight = (gridBit - gridBas) / 60 * SAAT_YUK;
  const saatLabelleri = Array.from({ length: (gridBit - gridBas) / 60 }, (_, i) => {
    const m = gridBas + i * 60;
    return `${String(m / 60 | 0).padStart(2,'0')}:00`;
  });

  // Günlük liste: dersler + tekrarlayan etkinlikler karışık
  const getItemsForGun = (gun: string) => {
    const items: any[] = [];
    dersler.forEach(d => {
      d.seanslar?.forEach((s:any) => {
        if (s.gun === gun) items.push({ _tip: 'ders', ...d, seans: s });
      });
    });
    tekrarlayanEtk.forEach(e => {
      if (e.gunler?.includes(gun)) {
        items.push({ _tip: 'etkinlik', ...e, seans: { baslangic_saat: e.baslangic_saat, bitis_saat: e.bitis_saat } });
      }
    });
    return items.sort((a,b) => String(a.seans.baslangic_saat).localeCompare(String(b.seans.baslangic_saat)));
  };

  const filtreliSalonlar = seciliSalon === 'tumu' ? salonlar : salonlar.filter((s:any) => s.id === seciliSalon);

  if (yukleniyor) return <div style={{ padding:24, color:textSec }}>Yukleniyor...</div>;

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>Haftalik program</h1>
        <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:`1px solid ${border}` }}>
          <button onClick={() => setGorunum('salon')}
            style={{ padding:'7px 16px', border:'none', cursor:'pointer', fontSize:13,
              background: gorunum==='salon' ? '#534AB7' : card,
              color: gorunum==='salon' ? 'white' : isDark ? '#bbb' : '#555' }}>
            Salon bazli
          </button>
          <button onClick={() => setGorunum('liste')}
            style={{ padding:'7px 16px', border:'none', borderLeft:`1px solid ${border}`, cursor:'pointer', fontSize:13,
              background: gorunum==='liste' ? '#534AB7' : card,
              color: gorunum==='liste' ? 'white' : isDark ? '#bbb' : '#555' }}>
            Gunluk liste
          </button>
        </div>
      </div>

      {gorunum === 'salon' ? (
        <>
          {/* Salon filtresi */}
          <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
            <button onClick={() => setSeciliSalon('tumu')}
              style={{ padding:'5px 14px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                background: seciliSalon==='tumu' ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                color: seciliSalon==='tumu' ? '#3C3489' : textSec,
                borderColor: seciliSalon==='tumu' ? '#534AB7' : '#ddd', fontWeight: seciliSalon==='tumu' ? 600 : 400 }}>
              Tumu
            </button>
            {salonlar.map((s:any, i:number) => {
              const r = RENKLER[i % RENKLER.length];
              const secili = seciliSalon === s.id;
              return (
                <button key={s.id} onClick={() => setSeciliSalon(s.id)}
                  style={{ padding:'5px 14px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                    background: secili ? r.bg : card,
                    color: secili ? r.text : textSec,
                    borderColor: secili ? r.bar : '#ddd', fontWeight: secili ? 600 : 400 }}>
                  {s.ad}
                </button>
              );
            })}
          </div>

          {/* Salon bazli grid */}
          {filtreliSalonlar.map((salon:any, si:number) => {
            const r = getSalonRenk(salon.id);
            return (
              <div key={salon.id} style={{ marginBottom:20 }}>
                {/* Salon baslik */}
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, padding:'8px 14px', borderRadius:8, background:r.bg }}>
                  <div style={{ width:28, height:28, borderRadius:6, background:r.bar, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600 }}>
                    {salon.ad.charAt(0)}
                  </div>
                  <div style={{ fontWeight:600, fontSize:13, color:r.text }}>{salon.ad}</div>
                  <span style={{ fontSize:11, color:r.text, opacity:.7 }}>Max {salon.kapasite} kisi{salon.konum ? ` · ${salon.konum}` : ''}</span>
                </div>

                {/* Zaman tabanlı grid */}
                <div style={{ background:card, border:`1px solid ${border}`, borderRadius:10, overflow:'auto' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'48px repeat(7, 1fr)', minWidth:560 }}>
                    {/* Gün başlıkları */}
                    <div style={{ padding:'8px 6px', borderBottom:`1px solid ${border}` }} />
                    {GUNLER.map(g => (
                      <div key={g} style={{
                        padding:'8px 6px', borderBottom:`1px solid ${border}`, borderLeft:`1px solid ${border}`,
                        textAlign:'center', fontSize:11, fontWeight:500,
                        background: g === bugunGun ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                        color: g === bugunGun ? '#3C3489' : textSec,
                      }}>
                        {g}
                      </div>
                    ))}

                    {/* Saat etiketi kolonu */}
                    <div style={{ position:'relative', height: gridHeight }}>
                      {saatLabelleri.map((saat, i) => (
                        <div key={saat} style={{
                          position:'absolute', top: i * SAAT_YUK - 7, right:4,
                          fontSize:10, color:textMuted, whiteSpace:'nowrap',
                        }}>{saat}</div>
                      ))}
                    </div>

                    {/* Gün kolonları */}
                    {GUNLER.map(gun => {
                      const gunDersler = dersler.filter(d =>
                        d.salon_id === salon.id &&
                        d.seanslar?.some((s:any) => s.gun === gun)
                      );
                      const gunEtk = tekrarlayanEtk.filter(e =>
                        e.salonlar?.some((s:any) => s.id === salon.id) &&
                        e.gunler?.includes(gun)
                      );
                      return (
                        <div key={gun} style={{
                          position:'relative', height: gridHeight,
                          borderLeft:`1px solid ${border}`,
                          background: gun === bugunGun ? isDark ? '#1c1c2e' : '#fafafe' : card,
                        }}>
                          {/* Saat çizgileri */}
                          {saatLabelleri.map((_, i) => (
                            <div key={i} style={{
                              position:'absolute', top: i * SAAT_YUK, left:0, right:0,
                              borderTop: i === 0 ? 'none' : `1px solid ${borderSub}`,
                            }} />
                          ))}
                          {/* Dersler */}
                          {gunDersler.map((d:any) => {
                            const seans = d.seanslar.find((s:any) => s.gun === gun);
                            const dr = RENKLER[dersler.indexOf(d) % RENKLER.length];
                            const top = (toMin(seans.baslangic_saat) - gridBas) / 60 * SAAT_YUK;
                            const height = Math.max((toMin(seans.bitis_saat) - toMin(seans.baslangic_saat)) / 60 * SAAT_YUK - 3, 18);
                            return (
                              <div key={d.id} onClick={() => navigate(`/dersler/${d.id}`)} style={{
                                position:'absolute', top, left:2, right:2, height,
                                background:dr.bg, color:dr.text,
                                borderRadius:4, padding:'3px 5px', fontSize:10,
                                cursor:'pointer', overflow:'hidden', lineHeight:1.3,
                              }}>
                                <div style={{ fontWeight:600 }}>{d.ad}</div>
                                <div style={{ opacity:.7, fontSize:9 }}>
                                  {String(seans.baslangic_saat).slice(0,5)}–{String(seans.bitis_saat).slice(0,5)}
                                </div>
                              </div>
                            );
                          })}
                          {/* Etkinlikler */}
                          {gunEtk.map((e:any) => {
                            const er = ETK_RENK[e.tip] || ETK_RENK.diger;
                            const bs = e.tum_gun ? gridBas : toMin(e.baslangic_saat);
                            const bt = e.tum_gun ? gridBit : toMin(e.bitis_saat);
                            const top = (bs - gridBas) / 60 * SAAT_YUK;
                            const height = Math.max((bt - bs) / 60 * SAAT_YUK - 3, 18);
                            return (
                              <div key={e.id} onClick={() => navigate('/etkinlikler')} style={{
                                position:'absolute', top, left:2, right:2, height,
                                background:er.bg, color:er.text,
                                borderRadius:4, padding:'3px 5px', fontSize:10,
                                cursor:'pointer', overflow:'hidden', lineHeight:1.3,
                                borderLeft:`3px solid ${er.bar}`,
                              }}>
                                <div style={{ fontWeight:600 }}>{e.ad}</div>
                                {!e.tum_gun && (
                                  <div style={{ opacity:.8, fontSize:9 }}>
                                    {String(e.baslangic_saat).slice(0,5)}–{String(e.bitis_saat).slice(0,5)}
                                  </div>
                                )}
                                <div style={{ fontSize:9, fontWeight:600, opacity:.7 }}>{ETK_LABEL[e.tip]}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <>
          {/* Gun secici */}
          <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
            {GUNLER.map(g => (
              <button key={g} onClick={() => setSeciliGun(g)}
                style={{ padding:'5px 14px', borderRadius:20, fontSize:12, cursor:'pointer', border:'1px solid',
                  background: seciliGun===g ? '#534AB7' : g===bugunGun ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                  color: seciliGun===g ? 'white' : g===bugunGun ? '#3C3489' : textSec,
                  borderColor: seciliGun===g ? '#534AB7' : g===bugunGun ? '#534AB7' : '#ddd',
                  fontWeight: seciliGun===g || g===bugunGun ? 600 : 400 }}>
                {g}{g===bugunGun ? ' (Bugun)' : ''}
              </button>
            ))}
          </div>

          {/* Gunluk ders + etkinlik listesi */}
          {getItemsForGun(seciliGun).length === 0 ? (
            <div style={{ background:card, border:'1px dashed #ddd', borderRadius:10, padding:32, textAlign:'center', color:textMuted }}>
              {GUN_LABELS[seciliGun]} günü ders ve etkinlik yok
            </div>
          ) : (
            <div style={{ display:'grid', gap:10 }}>
              {getItemsForGun(seciliGun).map((item:any, i:number) => {
                if (item._tip === 'etkinlik') {
                  const er = ETK_RENK[item.tip] || ETK_RENK.diger;
                  const etkSalonlar = item.salonlar?.map((s:any) => s.ad).join(', ') || '';
                  return (
                    <div key={`etk-${item.id}`} onClick={() => navigate('/etkinlikler')} style={{ background:card, border:`1px solid ${border}`, borderRadius:10, padding:16, display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}>
                      <div style={{ width:4, height:48, borderRadius:2, background:er.bar, flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                          <span style={{ fontWeight:600, fontSize:14 }}>{item.ad}</span>
                          <span style={{ fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:20, background:er.bg, color:er.text }}>{ETK_LABEL[item.tip]}</span>
                        </div>
                        <div style={{ fontSize:12, color:textSec }}>
                          {item.tum_gun ? 'Tüm gün' : `${String(item.seans.baslangic_saat).slice(0,5)} — ${String(item.seans.bitis_saat).slice(0,5)}`}
                          {etkSalonlar ? ` · ${etkSalonlar}` : ''}
                          {item.kiralayan ? ` · ${item.kiralayan}` : ''}
                        </div>
                      </div>
                      {item.bilet_aktif && (
                        <span style={{ fontSize:11, color:er.text, fontWeight:500 }}>🎟 {item.bilet_sayisi || 0}</span>
                      )}
                    </div>
                  );
                }
                // Ders
                const r = RENKLER[dersler.indexOf(dersler.find((x:any)=>x.id===item.id)) % RENKLER.length];
                const salon = salonlar.find((s:any) => s.id === item.salon_id);
                const egitmenler = item.egitmenler?.map((e:any) => e.egitmen_adi).join(', ') || 'Eğitmen atanmamış';
                return (
                  <div key={`ders-${item.id}-${i}`} onClick={() => navigate(`/dersler/${item.id}`)} style={{ background:card, border:`1px solid ${border}`, borderRadius:10, padding:16, display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}>
                    <div style={{ width:4, height:48, borderRadius:2, background:r.bar, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:14, marginBottom:3, color:textPrimary }}>{item.ad}</div>
                      <div style={{ fontSize:12, color:textSec }}>
                        {String(item.seans?.baslangic_saat).slice(0,5)} — {String(item.seans?.bitis_saat).slice(0,5)}
                        {salon ? ` · ${salon.ad}` : ''}
                        {` · ${egitmenler}`}
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                      <span style={{ background:r.bg, color:r.text, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:500 }}>
                        {item.seviye}
                      </span>
                      <span style={{ fontSize:11, color:textSec, fontWeight:500 }}>
                        {item.kayitli_ogrenci ?? 0}/{item.kapasite} öğrenci
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Program;
